import { useEffect, useState } from "react";
import { storage } from "../utils/storage";
import { Vehicle } from "../types";
import { Car, Plus, Edit2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { ScheduleGenerationStatus } from "./ScheduleGenerationStatus";

export function Vehicles() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeGenerations, setActiveGenerations] = useState<
    Record<string, { make: string; model: string; year: number }>
  >({});

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const data = await storage.getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error("Error loading vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData(e.currentTarget);

      const vehicleData = {
        make: formData.get("make") as string,
        model: formData.get("model") as string,
        year: parseInt(formData.get("year") as string),
        vin: formData.get("vin") as string,
        licensePlate: formData.get("licensePlate") as string,
        currentMileage: parseInt(formData.get("currentMileage") as string),
        color: formData.get("color") as string,
      };

      if (editingVehicle) {
        await storage.updateVehicle(editingVehicle.id, vehicleData);
      } else {
        const newVehicle: Vehicle = {
          id: crypto.randomUUID(),
          ...vehicleData,
          createdAt: new Date().toISOString(),
        };
        await storage.addVehicle(newVehicle);

        // Fire-and-forget schedule generation
        try {
          await storage.triggerScheduleGeneration(vehicleData.make, vehicleData.model, vehicleData.year);
          setActiveGenerations((prev) => ({
            ...prev,
            [newVehicle.id]: {
              make: vehicleData.make,
              model: vehicleData.model,
              year: vehicleData.year,
            },
          }));
          toast.success("Vehicle added! Generating maintenance schedule...");
        } catch {
          // Don't block vehicle creation if generation fails
          toast.success("Vehicle added!");
        }
      }

      await loadVehicles();
      setShowForm(false);
      setEditingVehicle(null);
    } catch (error) {
      console.error("Error saving vehicle:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (
      confirm(
        "Are you sure you want to delete this vehicle? All associated maintenance records will also be deleted."
      )
    ) {
      try {
        await storage.deleteVehicle(id);
        await loadVehicles();
      } catch (error) {
        console.error("Error deleting vehicle:", error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingVehicle(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Vehicles</h2>
          <p className="text-gray-600 mt-1">Manage your vehicles</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Vehicle
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingVehicle ? "Edit Vehicle" : "Add New Vehicle"}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Make *</label>
                <input
                  type="text"
                  name="make"
                  required
                  defaultValue={editingVehicle?.make}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Toyota"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                <input
                  type="text"
                  name="model"
                  required
                  defaultValue={editingVehicle?.model}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Camry"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                <input
                  type="number"
                  name="year"
                  required
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  defaultValue={editingVehicle?.year}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="2020"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Mileage *
                </label>
                <input
                  type="number"
                  name="currentMileage"
                  required
                  min="0"
                  defaultValue={editingVehicle?.currentMileage}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="50000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                <input
                  type="text"
                  name="vin"
                  defaultValue={editingVehicle?.vin}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1HGBH41JXMN109186"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  name="licensePlate"
                  defaultValue={editingVehicle?.licensePlate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ABC-1234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="text"
                  name="color"
                  defaultValue={editingVehicle?.color}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Silver"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editingVehicle ? "Update Vehicle" : "Add Vehicle"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vehicles.length === 0 && !showForm ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles yet</h3>
            <p className="text-gray-600 mb-6">Get started by adding your first vehicle</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Vehicle
            </button>
          </div>
        ) : (
          vehicles.map((vehicle) => (
            <div key={vehicle.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Car className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(vehicle)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(vehicle.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h3>
              {vehicle.color && <p className="text-sm text-gray-600 mb-3">{vehicle.color}</p>}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mileage:</span>
                  <span className="font-medium text-gray-900">
                    {vehicle.currentMileage.toLocaleString()} miles
                  </span>
                </div>
                {vehicle.licensePlate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">License Plate:</span>
                    <span className="font-medium text-gray-900">{vehicle.licensePlate}</span>
                  </div>
                )}
                {vehicle.vin && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">VIN:</span>
                    <span className="font-medium text-gray-900 text-xs">{vehicle.vin}</span>
                  </div>
                )}
              </div>
              {activeGenerations[vehicle.id] && (
                <ScheduleGenerationStatus
                  make={activeGenerations[vehicle.id].make}
                  model={activeGenerations[vehicle.id].model}
                  year={activeGenerations[vehicle.id].year}
                  vehicleId={vehicle.id}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
